import * as Dfns from 'date-fns/fp';
import { RunResultRow } from './use-linear-run-result';

// Excludes summary/total rows (empty `Network`) that runResult mixes in alongside real data
// rows -- those rows also lack fields like `Creative`, so this filter must run before touching
// row fields other than `Network`/`Cost`/`Number of Spots`.
export const nonEmptyNetworkRows = (rows: RunResultRow[]) => rows.filter(row => row.Network !== '');

const spendOf = (row: RunResultRow) => parseFloat(row.Cost) * parseFloat(row['Number of Spots']);
const sumSpend = (rows: RunResultRow[]) => rows.reduce((total, row) => total + spendOf(row), 0);

const groupBy = <T>(rows: T[], keyOf: (row: T) => string): Map<string, T[]> => {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const key = keyOf(row);
    const group = groups.get(key);
    if (group) {
      group.push(row);
    } else {
      groups.set(key, [row]);
    }
  }
  return groups;
};

// ---------------------------------------------------------------------------
// Network pivot
// ---------------------------------------------------------------------------

export interface NetworkPivotRow {
  network: string;
  avails: string;
  spend: number;
  pct: number;
}

export const buildNetworkPivot = (
  runResult: RunResultRow[]
): { rows: NetworkPivotRow[]; totalSpend: number } => {
  const scoped = nonEmptyNetworkRows(runResult);
  const totalSpend = sumSpend(scoped);
  const groups = groupBy(scoped, row => row.Network);

  const rows = [...groups.entries()]
    .map(([network, groupRows]) => {
      const spend = sumSpend(groupRows);
      return {
        network,
        avails: [...new Set(groupRows.map(row => row.Avail))].sort().join(', '),
        spend,
        pct: totalSpend > 0 ? spend / totalSpend : 0
      };
    })
    .sort((a, b) => b.spend - a.spend);

  return { rows, totalSpend };
};

// ---------------------------------------------------------------------------
// Daypart pivot -- weekday/weekend spend split
// ---------------------------------------------------------------------------

// [weekdayShare, weekendShare] per day/day-range value. Single weekdays and Saturday/Sunday get a
// pure 1/0 or 0/1 split; any range that spans into the weekend (e.g. "M - Sa", "F - Su") splits
// 5/7 weekday, 2/7 weekend regardless of how many weekdays it actually covers -- ported verbatim
// from the original bpm mapping, not a generic per-range day-count calculation.
const DAY_ALLOCATION_MAP: Record<string, [number, number]> = {
  M: [1, 0],
  'M - Tu': [1, 0],
  'M - W': [1, 0],
  'M - Th': [1, 0],
  'M - F': [1, 0],
  'M - Sa': [5 / 7, 2 / 7],
  'M - Su': [5 / 7, 2 / 7],
  Tu: [1, 0],
  'Tu - W': [1, 0],
  'Tu - Th': [1, 0],
  'Tu - F': [1, 0],
  'Tu - Sa': [5 / 7, 2 / 7],
  'Tu - Su': [5 / 7, 2 / 7],
  W: [1, 0],
  'W - Th': [1, 0],
  'W - F': [1, 0],
  'W - Sa': [5 / 7, 2 / 7],
  'W - Su': [5 / 7, 2 / 7],
  Th: [1, 0],
  'Th - F': [1, 0],
  'Th - Sa': [5 / 7, 2 / 7],
  'Th - Su': [5 / 7, 2 / 7],
  F: [1, 0],
  'F - Sa': [5 / 7, 2 / 7],
  'F - Su': [5 / 7, 2 / 7],
  Sa: [0, 1],
  'Sa - Su': [0, 1],
  Su: [0, 1]
};

const dayAllocation = (day: string): [number, number] => DAY_ALLOCATION_MAP[day] ?? [1, 0];

const DAYPART_NAME_ORDERING = [
  'Early Morning',
  'Daytime',
  'Early Fringe',
  'Prime',
  'Late Fringe',
  'Overnight',
  'Broad Rotator'
];

export interface DaypartPivotRow {
  daypart: string;
  weekday: number;
  weekend: number;
  pct: number;
}

const weekdayWeekendSpend = (rows: RunResultRow[]) =>
  rows.reduce(
    (totals, row) => {
      const spend = spendOf(row);
      const [weekdayShare, weekendShare] = dayAllocation(row.Day);
      return {
        spend: totals.spend + spend,
        weekday: totals.weekday + spend * weekdayShare,
        weekend: totals.weekend + spend * weekendShare
      };
    },
    { spend: 0, weekday: 0, weekend: 0 }
  );

export const buildDaypartPivot = (
  runResult: RunResultRow[],
  totalSpend: number
): { rows: DaypartPivotRow[]; totalsRow: DaypartPivotRow } => {
  const scoped = nonEmptyNetworkRows(runResult);
  const groups = groupBy(scoped, row => row['Daypart Name']);

  const rows = [...groups.entries()]
    .map(([daypart, groupRows]) => {
      const { spend, weekday, weekend } = weekdayWeekendSpend(groupRows);
      return {
        daypart,
        weekday: spend > 0 ? weekday / spend : 0,
        weekend: spend > 0 ? weekend / spend : 0,
        pct: totalSpend > 0 ? spend / totalSpend : 0
      };
    })
    .sort(
      (a, b) => DAYPART_NAME_ORDERING.indexOf(a.daypart) - DAYPART_NAME_ORDERING.indexOf(b.daypart)
    );

  // Computed over the same network-scoped rows as the per-daypart rows above, not the full
  // unfiltered runResult -- otherwise blank-Network footer rows fall through dayAllocation's
  // [1, 0] default and skew the total's weekday/weekend split away from the sum of the rows above.
  const { spend, weekday, weekend } = weekdayWeekendSpend(scoped);
  const totalsRow: DaypartPivotRow = {
    daypart: 'Total',
    weekday: spend > 0 ? weekday / spend : 0,
    weekend: spend > 0 ? weekend / spend : 0,
    pct: 1
  };

  return { rows, totalsRow };
};

// ---------------------------------------------------------------------------
// Avail-length pivot
// ---------------------------------------------------------------------------

export interface AvailLengthPivotRow {
  avail: string;
  spend: number;
  pct: number;
  pct60s?: number;
  pct30s?: number;
  pct15s?: number;
}

const spendForLength = (rows: RunResultRow[], length: string) =>
  sumSpend(rows.filter(row => row['Spot Length'] === length));

export const buildAvailLengthPivot = (
  runResult: RunResultRow[],
  totalSpend: number
): {
  rows: AvailLengthPivotRow[];
  totalsRow: AvailLengthPivotRow;
  showLength: Record<'60s' | '30s' | '15s', boolean>;
} => {
  const scoped = nonEmptyNetworkRows(runResult);
  const groups = groupBy(scoped, row => row.Avail);

  const rows = [...groups.entries()]
    .map(([avail, groupRows]) => {
      const spend = sumSpend(groupRows);
      return {
        avail,
        spend,
        pct: totalSpend > 0 ? spend / totalSpend : 0,
        // Divided by the pivot's overall totalSpend, not this avail group's own spend.
        pct60s: totalSpend > 0 ? spendForLength(groupRows, '60') / totalSpend : 0,
        pct30s: totalSpend > 0 ? spendForLength(groupRows, '30') / totalSpend : 0,
        pct15s: totalSpend > 0 ? spendForLength(groupRows, '15') / totalSpend : 0
      };
    })
    .sort((a, b) => a.avail.localeCompare(b.avail));

  const totalsRow: AvailLengthPivotRow = {
    avail: 'Total',
    spend: totalSpend,
    pct: 1,
    pct60s: totalSpend > 0 ? spendForLength(scoped, '60') / totalSpend : 0,
    pct30s: totalSpend > 0 ? spendForLength(scoped, '30') / totalSpend : 0,
    pct15s: totalSpend > 0 ? spendForLength(scoped, '15') / totalSpend : 0
  };

  const showLength = {
    '60s': rows.some(row => (row.pct60s ?? 0) > 0),
    '30s': rows.some(row => (row.pct30s ?? 0) > 0),
    '15s': rows.some(row => (row.pct15s ?? 0) > 0)
  };

  return { rows, totalsRow, showLength };
};

// ---------------------------------------------------------------------------
// Creative pivot
// ---------------------------------------------------------------------------

export interface CreativePivotRow {
  creative: string;
  spend: number;
  pct: number;
}

export const creativeDisplayName = (row: RunResultRow) =>
  row.Creative.split(';')[1] ?? row.Creative;

export const buildCreativePivot = (
  runResult: RunResultRow[],
  totalSpend: number
): { rows: CreativePivotRow[]; totalsRow: CreativePivotRow } => {
  const scoped = nonEmptyNetworkRows(runResult);
  const groups = groupBy(scoped, creativeDisplayName);

  const rows = [...groups.entries()]
    .map(([creative, groupRows]) => {
      const spend = sumSpend(groupRows);
      return { creative, spend, pct: totalSpend > 0 ? spend / totalSpend : 0 };
    })
    .sort((a, b) => a.creative.localeCompare(b.creative));

  return { rows, totalsRow: { creative: 'Total', spend: totalSpend, pct: 1 } };
};

// ---------------------------------------------------------------------------
// Buyable spots pivot
// ---------------------------------------------------------------------------

export interface BuyableSpotRow {
  network: string;
  avail: string;
  spotLength: string;
  day: string;
  daypart: string;
  daypartName: string;
  secured: string;
  cost: number;
  totalSpots: number;
  totalSpend: number;
  [creativeName: string]: string | number;
}

const COMPOSITE_KEY_FIELDS: (keyof RunResultRow)[] = [
  'Network',
  'Avail',
  'Spot Length',
  'Day',
  'Daypart',
  'Secured'
];
const compositeKey = (row: RunResultRow) => COMPOSITE_KEY_FIELDS.map(field => row[field]).join('_');

// An on-the-hour end time (e.g. "12:00PM") displays one minute earlier ("11:59AM") -- the buy
// itself runs through the top of the hour, but the on-air log shows the last full minute.
const displayDaypartEndTime = (daypart: string): string => {
  const [start, end] = daypart.split('-');
  if (!end) return daypart;
  try {
    const parsedEnd = Dfns.parse(new Date(), 'hh:mma', end.trim());
    const adjusted = Dfns.getMinutes(parsedEnd) === 0 ? Dfns.subMinutes(1, parsedEnd) : parsedEnd;
    return `${start.trim()} - ${Dfns.format('h:mma', adjusted)}`;
  } catch {
    return daypart;
  }
};

// BuyableSpotRow's own fields, in the same order as COMPOSITE_KEY_FIELDS -- used to sort the
// built rows deterministically (groupBy's Map iterates in first-seen order, not sorted order).
const buyableSpotSortKey = (row: BuyableSpotRow) =>
  [row.network, row.avail, row.spotLength, row.day, row.daypart, row.secured].join('_');

export const buildBuyableSpotsPivot = (
  runResult: RunResultRow[]
): { rows: BuyableSpotRow[]; creativeColumns: string[] } => {
  const scoped = nonEmptyNetworkRows(runResult);
  const creativeColumns = [...new Set(scoped.map(row => row.Creative))].sort();
  const groups = groupBy(scoped, compositeKey);

  const rows = [...groups.entries()]
    .map(([, groupRows]) => {
      const [first] = groupRows;
      const totalSpots = groupRows.reduce(
        (total, row) => total + parseInt(row['Number of Spots'], 10),
        0
      );
      const cost = parseFloat(first.Cost);
      const perCreativeSpots = Object.fromEntries(
        creativeColumns.map(creative => [
          creative,
          groupRows
            .filter(row => row.Creative === creative)
            .reduce((total, row) => total + parseInt(row['Number of Spots'], 10), 0)
        ])
      );
      return {
        network: first.Network,
        avail: first.Avail,
        spotLength: first['Spot Length'],
        day: first.Day,
        daypart: displayDaypartEndTime(first.Daypart),
        daypartName: first['Daypart Name'],
        secured: first.Secured,
        cost,
        totalSpots,
        totalSpend: totalSpots * cost,
        ...perCreativeSpots
      };
    })
    .sort((a, b) => buyableSpotSortKey(a).localeCompare(buyableSpotSortKey(b)));

  return { rows, creativeColumns };
};

const BUYABLE_TOTALS_EXCLUDED_KEYS = new Set([
  'network',
  'avail',
  'day',
  'daypart',
  'daypartName',
  'Rotation ID',
  'Creative',
  'spotLength',
  'secured',
  'cost',
  'Number of Spots',
  'totalSpots',
  'totalSpend'
]);

export interface BuyableTotalsRow {
  totalSpend: string;
  totalSpots: number;
  [creativeName: string]: string | number;
}

// Computed from the currently-*filtered* rows (not the full pivot) -- the totals row reflects
// whatever the user's filter bar currently shows. `totalSpend` is deliberately a "$..." string
// (not a number) so the shared totals renderer can detect and reformat it; changing this to a
// number would silently break that renderer.
export const buildBuyableTotalsRow = (filteredRows: BuyableSpotRow[]): BuyableTotalsRow => {
  const totalSpend = filteredRows.reduce((total, row) => total + row.totalSpend, 0);
  const totalSpots = filteredRows.reduce((total, row) => total + row.totalSpots, 0);
  const perCreativeTotals = Object.fromEntries(
    Object.keys(filteredRows[0] ?? {})
      .filter(key => !BUYABLE_TOTALS_EXCLUDED_KEYS.has(key))
      .map(key => [key, filteredRows.reduce((total, row) => total + (Number(row[key]) || 0), 0)])
  );
  return {
    network: 'Total',
    totalSpend: `$${totalSpend.toFixed(2)}`,
    totalSpots,
    ...perCreativeTotals
  };
};
